import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { motion } from 'framer-motion';
import { Calendar as CalendarIcon, ChevronLeft, ChevronRight, Plus, Upload, X, Trash2, Save, FileVideo, Image as ImageIcon, FileText, Loader2, History as ClockHistory, MessageSquare, Send, Lock, UserCheck, Check, PenSquare, BellRing } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { toast } from 'sonner';
import { contentCalendarAPI, teamAPI, metaSocialAPI } from '../lib/api';
import { useAuthStore } from '../store';
import { CONTENT_STATES, getStateMeta, CONTENT_TYPES, getPostChannels, CONTENT_PILLARS, getPillarMeta, matchPillar } from '../lib/contentStates';
import { ChannelMultiSelect, ChannelIconsRow, ChannelLabels } from './ChannelIcons';
import GuionTableEditor, { legacyGuionToRows } from './GuionTableEditor';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip';
import { Switch } from './ui/switch';
import { Checkbox } from './ui/checkbox';
import AssignChangesPanel from './AssignChangesPanel';
import { TeamInstagramPreviewDialog } from './InstagramPreview';
import { useMedia } from './ApprovalReview';
import { brandKitAPI } from '../lib/api';
import { Dialog as IgDialog, DialogContent as IgDialogContent, DialogHeader as IgDialogHeader, DialogTitle as IgDialogTitle } from './ui/dialog';
import InternalCommentsThread, { CommentsBadge } from './InternalCommentsThread';
import { MetaPublishCard } from './MetaPublishCard';
import { META_DIRECT_ENABLED } from '../lib/features';
import { internalCommentsAPI } from '../lib/api';
import { PostDownloadMenu, DownloadSelectionBar, useDownloadSelection, triggerFileDownload, downloadPostsZip } from './PostDownloadTools';
import { Zap, Video, Palette, Download, Archive, Instagram } from 'lucide-react';
import { useCalendarVisibleFields, CalendarFieldsSelector, PostHoverCard, PostCalendarCard } from './CalendarCellFields';
import { CsvImportButton } from './CsvImportButton';
import { Maximize2, Minimize2 } from 'lucide-react';

const SPANISH_DAYS = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'];
const SPANISH_MONTHS = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];

// Build calendar grid (Monday-first) for any month
function buildMonthGrid(year, month) {
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);
  // JS getDay(): 0=Sun..6=Sat → convert to Monday-first index (0=Mon..6=Sun)
  const firstWeekday = (firstDay.getDay() + 6) % 7;
  const totalDays = lastDay.getDate();
  const cells = [];
  for (let i = 0; i < firstWeekday; i++) cells.push(null);
  for (let d = 1; d <= totalDays; d++) cells.push(new Date(year, month, d));
  while (cells.length % 7 !== 0) cells.push(null);
  return cells;
}

function ymd(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function StateBadge({ stateId, className = '' }) {
  const meta = getStateMeta(stateId);
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-medium leading-tight whitespace-nowrap ${className}`}
      style={{ backgroundColor: meta.bg, color: meta.fg }}
      data-testid={`state-badge-${stateId}`}
    >
      {meta.label}
    </span>
  );
}

function timeAgoEs(iso) {
  if (!iso) return '';
  const then = new Date(iso);
  if (isNaN(then.getTime())) return '';
  const diffSec = Math.max(0, Math.floor((Date.now() - then.getTime()) / 1000));
  if (diffSec < 60) return 'hace unos segundos';
  const m = Math.floor(diffSec / 60);
  if (m < 60) return `hace ${m} ${m === 1 ? 'minuto' : 'minutos'}`;
  const h = Math.floor(m / 60);
  if (h < 24) return `hace ${h} ${h === 1 ? 'hora' : 'horas'}`;
  const d = Math.floor(h / 24);
  if (d < 7) return `hace ${d} ${d === 1 ? 'día' : 'días'}`;
  if (d < 30) {
    const w = Math.floor(d / 7);
    return `hace ${w} ${w === 1 ? 'semana' : 'semanas'}`;
  }
  // Fallback to absolute date
  return then.toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: '2-digit' });
}

function StateHistoryPanel({ history = [], userMap = {} }) {
  if (!history || history.length === 0) {
    return (
      <p className="text-xs text-muted-foreground py-3 text-center border border-dashed rounded-md" data-testid="state-history-empty">
        Aún no hay cambios registrados.
      </p>
    );
  }
  // Newest first
  const ordered = [...history].slice().reverse();
  return (
    <div className="space-y-2" data-testid="state-history-panel">
      {ordered.map((h, i) => {
        const who = userMap[h.by_user_id] || 'Alguien';
        const when = h.at ? timeAgoEs(h.at) : '';
        const absDate = h.at ? new Date(h.at).toLocaleString('es-ES') : '';
        return (
          <div key={i} className="relative pl-5 pb-2 last:pb-0" data-testid={`history-entry-${i}`}>
            {/* Vertical line + dot */}
            <span className="absolute left-1.5 top-1.5 w-1.5 h-1.5 rounded-full bg-primary" />
            {i < ordered.length - 1 && <span className="absolute left-[9px] top-3 bottom-0 w-px bg-border" />}
            <p className="text-xs font-medium text-foreground leading-tight">
              <span className="font-semibold">{who}</span>{' '}
              {h.from ? (
                <>cambió de <StateBadge stateId={h.from} /> → <StateBadge stateId={h.to} /></>
              ) : (
                <>creó la publicación en <StateBadge stateId={h.to} /></>
              )}
            </p>
            <p className="text-[10px] text-muted-foreground mt-0.5" title={absDate}>{when}</p>
          </div>
        );
      })}
    </div>
  );
}

export default function ClientContentCalendar({ clientId, clientName, initialPostId = null, onInitialPostOpened }) {
  const [posts, setPosts] = useState([]);
  const [team, setTeam] = useState([]);
  const [fullscreen, setFullscreen] = useState(true);
  useEffect(() => {
    if (!fullscreen) return undefined;
    const onKey = (e) => { if (e.key === 'Escape' && !document.querySelector('[role="dialog"]')) setFullscreen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [fullscreen]);
  const [loading, setLoading] = useState(true);
  const [cursor, setCursor] = useState(() => {
    const now = new Date();
    return { year: now.getFullYear(), month: now.getMonth() };
  });
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [obsReply, setObsReply] = useState('');
  const [sendingObs, setSendingObs] = useState(false);

  const { user } = useAuthStore();
  const isAdmin = ['ceo', 'manager'].includes(user?.role);
  const clientMode = user?.role === 'client';
  // Todo el equipo interno edita cualquier publicación (trabajo cruzado); los clientes solo aprueban/piden cambios
  const canEditPost = (post) => !clientMode && !!user && user.role !== 'client';
  // Eliminar sigue reservado a CEO/Manager o a la persona responsable de la publicación
  const canDeletePost = (post) => isAdmin || (!!post?.responsable_id && post.responsable_id === user?.id) || (!!post?.responsable_portada_id && post.responsable_portada_id === user?.id);
  const [onlyMine, setOnlyMine] = useState(false);
  const [reqOpen, setReqOpen] = useState(false);
  const [reqText, setReqText] = useState('');
  const [acting, setActing] = useState(false);
  const [sendingReminder, setSendingReminder] = useState(false);
  const monthAutoSet = useRef(false);

  const pendingApprovalCount = posts.filter((p) => p.estado === 'pendiente_aprobar').length;
  const handleSendReminder = async () => {
    setSendingReminder(true);
    try {
      const r = await contentCalendarAPI.sendApprovalReminder(clientId);
      if (r?.sent) {
        const via = (r.via || []).includes('discord') ? 'Discord' : 'email';
        toast.success(`Recordatorio enviado por ${via} (${r.posts} publicación${r.posts > 1 ? 'es' : ''})`);
      } else {
        toast.info(r?.message || 'No se envió el recordatorio');
      }
    } catch (e) { toast.error(e?.message || 'Error enviando el recordatorio'); }
    finally { setSendingReminder(false); }
  };

  const doApprove = async (post) => {
    setActing(true);
    try {
      await contentCalendarAPI.approve(post.id);
      toast.success('Publicación aprobada');
      await loadPosts();
      setEditorOpen(false); setEditingPost(null);
    } catch (e) { toast.error(e?.message || 'Error al aprobar'); }
    finally { setActing(false); }
  };
  const doRequestChanges = async () => {
    const t = reqText.trim();
    if (!editingPost || !t) return;
    setActing(true);
    try {
      await contentCalendarAPI.requestChanges(editingPost.id, t);
      toast.success('Cambios solicitados');
      setReqOpen(false); setReqText('');
      await loadPosts();
      setEditorOpen(false); setEditingPost(null);
    } catch (e) { toast.error(e?.message || 'Error solicitando cambios'); }
    finally { setActing(false); }
  };

  const handleSendObservation = async () => {
    const texto = obsReply.trim();
    if (!editingPost || editingPost._isNew || !texto) return;
    setSendingObs(true);
    try {
      await contentCalendarAPI.addObservation(editingPost.id, texto);
      const refreshed = await contentCalendarAPI.get(editingPost.id);
      setEditingPost(refreshed);
      setPosts((prev) => prev.map((p) => p.id === refreshed.id ? refreshed : p));
      setObsReply('');
    } catch (e) {
      toast.error(e?.message || 'Error enviando la observación');
    } finally {
      setSendingObs(false);
    }
  };

  const loadPosts = useCallback(async () => {
    setLoading(true);
    try {
      const [data, teamData] = await Promise.all([
        contentCalendarAPI.getAll(clientId),
        clientMode ? Promise.resolve([]) : teamAPI.getAll().catch(() => []),
      ]);
      setPosts(Array.isArray(data) ? data : []);
      setTeam(Array.isArray(teamData) ? teamData : (teamData?.members || []));
    } catch (e) {
      console.error(e);
      toast.error('Error cargando el calendario');
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  const [dragOver, setDragOver] = useState(null);
  const movePost = async (postId, fecha) => {
    const p = posts.find((x) => x.id === postId);
    if (!p || p.fecha === fecha) return;
    const prev = posts;
    setPosts((ps) => ps.map((x) => (x.id === postId ? { ...x, fecha } : x)));
    try {
      if (p.estado === 'programado') {
        const r = await metaSocialAPI.move(postId, fecha);
        toast.success(r.rescheduled ? 'Movida y reprogramada en Meta a la misma hora' : 'Publicación movida');
      } else {
        await contentCalendarAPI.update(postId, { fecha });
        toast.success('Publicación movida');
      }
    } catch (e) {
      setPosts(prev);
      toast.error(e.message);
    }
  };

  useEffect(() => {
    loadPosts();
  }, [loadPosts]);

  // Sincronización en tiempo real: refresco silencioso cada 15s
  useEffect(() => {
    const id = setInterval(async () => {
      try {
        const data = await contentCalendarAPI.getAll(clientId);
        setPosts(Array.isArray(data) ? data : []);
      } catch (_) { /* ignore */ }
    }, 15000);
    return () => clearInterval(id);
  }, [clientId]);

  // Auto-open a post (used when navigating from PublicationsPage)
  useEffect(() => {
    if (!initialPostId || posts.length === 0) return;
    const target = posts.find((p) => p.id === initialPostId);
    if (target) {
      setEditingPost({ ...target });
      setEditorOpen(true);
      // Navigate calendar cursor to the post's month
      try {
        const d = new Date(target.fecha + 'T00:00:00');
        if (!isNaN(d.getTime())) setCursor({ year: d.getFullYear(), month: d.getMonth() });
      } catch (_) {
        /* ignore date parse errors */
      }
      onInitialPostOpened?.();
    }
  }, [initialPostId, posts, onInitialPostOpened]);

  const cells = useMemo(() => buildMonthGrid(cursor.year, cursor.month), [cursor]);
  const selection = useDownloadSelection();
  const [commentInfo, setCommentInfo] = useState({});
  const [igOpen, setIgOpen] = useState(false);
  useEffect(() => {
    if (clientMode || !clientId) return;
    internalCommentsAPI.unread(clientId).then((r) => setCommentInfo(r || {})).catch(() => {});
  }, [clientId, clientMode]);
  const markCommentsRead = useCallback((pid) => setCommentInfo((prev) => (prev[pid] ? { ...prev, [pid]: { ...prev[pid], unread: 0 } } : prev)), []);

  // En modo cliente: si el mes actual no tiene publicaciones pero hay en otros meses,
  // saltamos una vez al mes con la publicación más cercana a hoy (para no ver el calendario vacío).
  useEffect(() => {
    if (!clientMode || monthAutoSet.current || posts.length === 0) return;
    const hasCurrent = posts.some((p) => {
      const d = new Date((p.fecha || '') + 'T00:00:00');
      return !isNaN(d.getTime()) && d.getFullYear() === cursor.year && d.getMonth() === cursor.month;
    });
    if (!hasCurrent) {
      const now = new Date();
      const dated = posts.filter((p) => p.fecha).map((p) => new Date(p.fecha + 'T00:00:00')).filter((d) => !isNaN(d.getTime()));
      if (dated.length) {
        dated.sort((a, b) => Math.abs(a - now) - Math.abs(b - now));
        const d = dated[0];
        setCursor({ year: d.getFullYear(), month: d.getMonth() });
      }
    }
    monthAutoSet.current = true;
  }, [posts, clientMode, cursor]);

  const postsByDate = useMemo(() => {
    const map = {};
    for (const p of posts) {
      if (onlyMine && p.responsable_id !== user?.id) continue;
      const key = (p.fecha || '').slice(0, 10);
      if (!key) continue;
      if (!map[key]) map[key] = [];
      map[key].push(p);
    }
    return map;
  }, [posts, onlyMine, user]);

  const monthPosts = useMemo(() => {
    const prefix = `${cursor.year}-${String(cursor.month + 1).padStart(2, '0')}`;
    return Object.keys(postsByDate).filter((d) => d.startsWith(prefix)).sort().flatMap((d) => postsByDate[d]);
  }, [postsByDate, cursor]);

  const goPrev = () => {
    setCursor((c) => {
      const m = c.month - 1;
      if (m < 0) return { year: c.year - 1, month: 11 };
      return { year: c.year, month: m };
    });
  };
  const goNext = () => {
    setCursor((c) => {
      const m = c.month + 1;
      if (m > 11) return { year: c.year + 1, month: 0 };
      return { year: c.year, month: m };
    });
  };
  const goToday = () => {
    const now = new Date();
    setCursor({ year: now.getFullYear(), month: now.getMonth() });
  };

  const openCreate = (dateStr) => {
    setEditingPost({
      _isNew: true,
      fecha: dateStr,
      estado: 'idea',
      titulo: '',
      tipo: '',
      copy: '',
      guion: '',
      instrucciones: '',
      pilar: '',
      canal: '',
      canales: [],
      responsable_id: '',
      responsable_portada_id: '',
      client_id: clientId,
    });
    setEditorOpen(true);
  };

  const openEdit = (post) => {
    setEditingPost({ ...post, canales: getPostChannels(post) });
    setEditorOpen(true);
    contentCalendarAPI.get(post.id)
      .then((full) => setEditingPost((cur) => (cur && cur.id === post.id ? { ...cur, attachments: full.attachments || [] } : cur)))
      .catch(() => {});
  };

  const handleSavePost = async () => {
    if (!editingPost) return;
    if (!editingPost.fecha) {
      toast.error('Selecciona una fecha');
      return;
    }
    try {
      const payload = {
        fecha: editingPost.fecha,
        estado: editingPost.estado || 'idea',
        titulo: editingPost.titulo || null,
        tipo: editingPost.tipo || null,
        copy: editingPost.copy || null,
        guion: editingPost.guion || null,
        guion_table: Array.isArray(editingPost.guion_table) ? editingPost.guion_table : null,
        instrucciones: editingPost.instrucciones || null,
        pilar: editingPost.pilar || null,
        canales: Array.isArray(editingPost.canales) ? editingPost.canales : getPostChannels(editingPost),
        automatizacion: !!editingPost.automatizacion,
        quien_graba: (editingPost.quien_graba || '').trim(),
        pilar: matchPillar(editingPost.pilar)?.id || editingPost.pilar || '',
        responsable_id: editingPost.responsable_id || null,
        responsable_portada_id: editingPost.responsable_portada_id || null,
        client_id: clientId,
      };
      if (editingPost._isNew) {
        const created = await contentCalendarAPI.create(payload);
        setPosts((prev) => [...prev, created]);
        toast.success('Publicación creada');
        setEditingPost(created);  // switch to edit mode so attachments can be uploaded
      } else {
        const orig = posts.find((x) => x.id === editingPost.id);
        if (orig && orig.estado === 'programado' && payload.fecha && payload.fecha !== orig.fecha) {
          await metaSocialAPI.move(editingPost.id, payload.fecha);
        }
        const updated = await contentCalendarAPI.update(editingPost.id, payload);
        setPosts((prev) => prev.map((p) => (p.id === updated.id ? updated : p)));
        toast.success('Publicación guardada');
        setEditingPost((cur) => ({ ...updated, attachments: cur?.attachments || [] }));
      }
    } catch (e) {
      toast.error(e?.message || 'Error guardando');
    }
  };

  const handleDelete = async () => {
    if (!editingPost || editingPost._isNew) return;
    if (!window.confirm('¿Eliminar esta publicación? Esta acción no se puede deshacer.')) return;
    try {
      await contentCalendarAPI.delete(editingPost.id);
      setPosts((prev) => prev.filter((p) => p.id !== editingPost.id));
      setEditorOpen(false);
      setEditingPost(null);
      toast.success('Publicación eliminada');
    } catch (e) {
      toast.error('Error eliminando');
    }
  };

  const [visibleFields, toggleVisibleField] = useCalendarVisibleFields();
  // Nombres ya usados en "Quién graba" para este cliente (autocompletado)
  const quienGrabaOptions = React.useMemo(() => Array.from(new Set(posts.map((p) => (p.quien_graba || '').trim()).filter(Boolean))).sort((a, b) => a.localeCompare(b, 'es')), [posts]);

  const coverInputRef = useRef(null);
  const colorCoverInputRef = useRef(null);
  const [uploadingCover, setUploadingCover] = useState(false);
  const [uploadingColorCover, setUploadingColorCover] = useState(false);
  // Sube una imagen y la fija en el campo indicado (portada_file_id | color_portada_file_id)
  const uploadImageField = async (e, field, label, setBusy) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !editingPost || editingPost._isNew) return;
    if (!file.type.startsWith('image/')) { toast.error(`${label} debe ser una imagen`); return; }
    setBusy(true);
    try {
      const att = await contentCalendarAPI.uploadAttachment(editingPost.id, file);
      await contentCalendarAPI.update(editingPost.id, { [field]: att.id });
      const refreshed = await contentCalendarAPI.get(editingPost.id);
      setEditingPost(refreshed);
      setPosts((prev) => prev.map((p) => (p.id === refreshed.id ? refreshed : p)));
      toast.success(`${label} subida`);
    } catch (err) {
      toast.error(err?.message || `Error subiendo ${label.toLowerCase()}`);
    } finally {
      setBusy(false);
    }
  };
  const handleCoverUpload = (e) => uploadImageField(e, 'portada_file_id', 'Portada', setUploadingCover);
  const handleColorCoverUpload = (e) => uploadImageField(e, 'color_portada_file_id', 'Color de portada', setUploadingColorCover);
  const handleColorCoverRemove = async () => {
    if (!editingPost || editingPost._isNew) return;
    try {
      await contentCalendarAPI.update(editingPost.id, { color_portada_file_id: '' });
      setEditingPost((p) => ({ ...p, color_portada_file_id: '' }));
      setPosts((prev) => prev.map((p) => (p.id === editingPost.id ? { ...p, color_portada_file_id: '' } : p)));
      toast.success('Color de portada quitado');
    } catch (e) {
      toast.error('Error quitando el color de portada');
    }
  };

  const handleCoverRemove = async () => {
    if (!editingPost || editingPost._isNew) return;
    try {
      await contentCalendarAPI.update(editingPost.id, { portada_file_id: '' });
      setEditingPost((p) => ({ ...p, portada_file_id: '' }));
      setPosts((prev) => prev.map((p) => (p.id === editingPost.id ? { ...p, portada_file_id: '' } : p)));
      toast.success('Portada quitada');
    } catch (err) {
      toast.error(err?.message || 'Error quitando la portada');
    }
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !editingPost || editingPost._isNew) return;
    try {
      toast.info('Subiendo archivo...');
      await contentCalendarAPI.uploadAttachment(editingPost.id, file);
      const refreshed = await contentCalendarAPI.get(editingPost.id);
      setEditingPost(refreshed);
      setPosts((prev) => prev.map((p) => (p.id === refreshed.id ? refreshed : p)));
      toast.success('Archivo subido');
    } catch (err) {
      toast.error(err?.message || 'Error subiendo');
    } finally {
      e.target.value = '';
    }
  };

  const handleDeleteAttachment = async (fileId) => {
    if (!editingPost || editingPost._isNew) return;
    if (!window.confirm('¿Eliminar este archivo?')) return;
    try {
      await contentCalendarAPI.deleteAttachment(editingPost.id, fileId);
      const refreshed = await contentCalendarAPI.get(editingPost.id);
      setEditingPost(refreshed);
      setPosts((prev) => prev.map((p) => (p.id === refreshed.id ? refreshed : p)));
      toast.success('Archivo eliminado');
    } catch {
      toast.error('Error eliminando archivo');
    }
  };

  const attachments = editingPost?.attachments || [];

  // Map user_id -> name for the state history panel
  const teamNameMap = team.reduce((acc, m) => {
    if (m?.id) acc[m.id] = m.name || m.email || 'Usuario';
    return acc;
  }, {});

  // Full-page editor view (replaces calendar grid when editing/creating)
  if (editorOpen && editingPost) {
    const editable = editingPost._isNew ? isAdmin : canEditPost(editingPost);
    return (
      <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} className="space-y-4" data-testid="post-editor-page">
        {/* Sticky-ish header */}
        <Card>
          <CardContent className="py-3">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div className="flex items-center gap-3 min-w-0">
                <Button variant="ghost" size="sm" onClick={() => { setEditorOpen(false); setEditingPost(null); }} data-testid="post-editor-back-btn">
                  <ChevronLeft className="w-4 h-4 mr-1" /> Volver al calendario
                </Button>
                <div className="h-6 w-px bg-border" />
                <div className="min-w-0">
                  <h2 className="text-base font-semibold truncate">
                    {editingPost._isNew ? 'Nueva publicación' : (editingPost.titulo || 'Sin título')}
                  </h2>
                  <p className="text-xs text-muted-foreground flex flex-wrap items-center gap-1.5">{clientName} · {editingPost.fecha} <ChannelLabels channels={editingPost.canales || getPostChannels(editingPost)} testId="post-header-channels" />{editingPost.automatizacion && <Badge variant="outline" className="text-[10px] border-amber-300 text-amber-700 bg-amber-50"><Zap className="w-3 h-3 mr-0.5" /> Automatización</Badge>}{editingPost.quien_graba && <Badge variant="outline" className="text-[10px]"><Video className="w-3 h-3 mr-0.5" /> {editingPost.quien_graba}</Badge>}{getPillarMeta(editingPost.pilar) && <Badge variant="outline" className="text-[10px] text-white border-transparent" style={{ backgroundColor: getPillarMeta(editingPost.pilar).color }} data-testid="post-header-pilar">{getPillarMeta(editingPost.pilar).label}</Badge>}</p>
                </div>
                {editingPost?.estado && <StateBadge stateId={editingPost.estado} />}
              </div>
              {editingPost?.nota_grabacion?.texto && (
                <div className="flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900" data-testid="nota-grabacion-banner">
                  <Video className="w-4 h-4 mt-0.5 shrink-0" />
                  <div>
                    <p className="font-medium">Nota de grabación de {editingPost.nota_grabacion.por}{editingPost.nota_grabacion.at ? ` · ${new Date(editingPost.nota_grabacion.at).toLocaleDateString('es-ES')}` : ''}</p>
                    <p className="whitespace-pre-wrap">{editingPost.nota_grabacion.texto}</p>
                  </div>
                </div>
              )}
              <div className="flex gap-2 items-center">
                {!editable && !clientMode && (
                  <span className="inline-flex items-center gap-1.5 text-xs font-medium text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-2.5 py-1.5" data-testid="post-readonly-badge">
                    <Lock className="w-3.5 h-3.5" /> Solo lectura · no asignada a ti
                  </span>
                )}
                {clientMode && editingPost.estado === 'pendiente_aprobar' && (
                  <>
                    <Button variant="outline" onClick={() => { setReqOpen(true); setReqText(''); }} disabled={acting} data-testid="client-request-changes-btn">
                      <PenSquare className="w-4 h-4 mr-1.5" /> Solicitar cambios
                    </Button>
                    <Button onClick={() => doApprove(editingPost)} disabled={acting} className="bg-emerald-600 hover:bg-emerald-700" data-testid="client-approve-btn">
                      {acting ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Check className="w-4 h-4 mr-1.5" />} Aprobar
                    </Button>
                  </>
                )}
                {clientMode && editingPost.estado !== 'pendiente_aprobar' && (
                  <span className="inline-flex items-center gap-1.5 text-xs font-medium text-muted-foreground bg-muted border rounded-md px-2.5 py-1.5" data-testid="post-readonly-badge">
                    <Lock className="w-3.5 h-3.5" /> Solo lectura
                  </span>
                )}
                {editable && !editingPost._isNew && canDeletePost(editingPost) && (
                  <Button variant="ghost" onClick={handleDelete} className="text-destructive hover:text-destructive" data-testid="post-delete-btn">
                    <Trash2 className="w-4 h-4 mr-1.5" /> Eliminar
                  </Button>
                )}
                {editable && (
                  <Button onClick={handleSavePost} data-testid="post-save-btn">
                    <Save className="w-4 h-4 mr-1.5" />
                    {editingPost._isNew ? 'Crear publicación' : 'Guardar cambios'}
                  </Button>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {clientMode && reqOpen && (
          <Card data-testid="client-request-changes-panel">
            <CardContent className="py-3 space-y-2">
              <Label className="text-sm font-medium">¿Qué te gustaría cambiar?</Label>
              <Textarea value={reqText} onChange={(e) => setReqText(e.target.value)} rows={3} className="resize-none" placeholder="Describe los cambios que quieres en esta publicación..." data-testid="client-changes-text" />
              <div className="flex gap-2 justify-end">
                <Button variant="ghost" onClick={() => { setReqOpen(false); setReqText(''); }}>Cancelar</Button>
                <Button onClick={doRequestChanges} disabled={acting || !reqText.trim()} data-testid="client-changes-submit">
                  {acting ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Send className="w-4 h-4 mr-1.5" />} Enviar
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Top row: Información + Adjuntos */}
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
          {/* Información (2 cols) */}
          <Card className="xl:col-span-2">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm">Información de la publicación</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <fieldset disabled={!editable} className="border-0 p-0 m-0 min-w-0 space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Fecha</Label>
                  <Input
                    type="date"
                    value={editingPost.fecha || ''}
                    onChange={(e) => setEditingPost((p) => ({ ...p, fecha: e.target.value }))}
                    data-testid="post-fecha-input"
                  />
                </div>
                <div>
                  <Label>Estado</Label>
                  <Select value={editingPost.estado || 'idea'} onValueChange={(v) => setEditingPost((p) => ({ ...p, estado: v }))}>
                    <SelectTrigger data-testid="post-estado-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {CONTENT_STATES.map((s) => (
                        <SelectItem key={s.id} value={s.id}>
                          <span className="inline-flex items-center gap-2">
                            <span className="inline-block w-3 h-3 rounded-sm" style={{ backgroundColor: s.bg, border: `1px solid ${s.fg}30` }} />
                            {s.label}
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label>Título</Label>
                <Input
                  value={editingPost.titulo || ''}
                  onChange={(e) => setEditingPost((p) => ({ ...p, titulo: e.target.value }))}
                  placeholder="Título descriptivo de la publicación"
                  data-testid="post-titulo-input"
                />
              </div>

              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label>Tipo</Label>
                  <Select value={editingPost.tipo || '__none__'} onValueChange={(v) => setEditingPost((p) => ({ ...p, tipo: v === '__none__' ? '' : v }))}>
                    <SelectTrigger data-testid="post-tipo-select"><SelectValue placeholder="Tipo" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">— Sin tipo —</SelectItem>
                      {CONTENT_TYPES.map((t) => <SelectItem key={t.id} value={t.id}>{t.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="sm:col-span-2 lg:col-span-1">
                  <Label>Canales <span className="text-muted-foreground font-normal">(puedes marcar varios)</span></Label>
                  <div className="mt-1.5">
                    <ChannelMultiSelect value={editingPost.canales || []} onChange={(v) => setEditingPost((p) => ({ ...p, canales: v, canal: v[0] || '' }))} />
                  </div>
                </div>
                <div>
                  <Label>Pilar de contenido</Label>
                  <Select
                    value={matchPillar(editingPost.pilar)?.id || (editingPost.pilar ? '__legacy__' : '__none__')}
                    onValueChange={(v) => setEditingPost((p) => ({ ...p, pilar: v === '__none__' ? '' : v }))}
                  >
                    <SelectTrigger data-testid="post-pilar-select"><SelectValue placeholder="Pilar" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">— Sin pilar —</SelectItem>
                      {editingPost.pilar && !matchPillar(editingPost.pilar) && (
                        <SelectItem value="__legacy__" disabled>Valor antiguo: "{editingPost.pilar}" (elige uno de la lista)</SelectItem>
                      )}
                      {CONTENT_PILLARS.map((pl) => (
                        <SelectItem key={pl.id} value={pl.id} data-testid={`post-pilar-option-${pl.id}`}>
                          <span className="inline-flex items-center gap-2"><span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: pl.color }} />{pl.label}</span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label className="flex items-center gap-1.5"><Zap className="w-3.5 h-3.5 text-amber-500" /> Automatización</Label>
                  <div className="flex items-center gap-2 mt-2">
                    <Switch checked={!!editingPost.automatizacion} onCheckedChange={(v) => setEditingPost((p) => ({ ...p, automatizacion: v }))} data-testid="post-automatizacion-switch" />
                    <span className="text-sm text-muted-foreground" data-testid="post-automatizacion-label">{editingPost.automatizacion ? 'Sí, tiene automatización' : 'No'}</span>
                  </div>
                </div>
                <div>
                  <Label className="flex items-center gap-1.5"><Video className="w-3.5 h-3.5 text-primary" /> Quién graba <span className="text-muted-foreground font-normal">(persona del cliente)</span></Label>
                  <Input
                    list="quien-graba-options"
                    value={editingPost.quien_graba || ''}
                    onChange={(e) => setEditingPost((p) => ({ ...p, quien_graba: e.target.value }))}
                    placeholder="Nombre de quien graba el reel"
                    data-testid="post-quien-graba-input"
                  />
                  <datalist id="quien-graba-options">
                    {quienGrabaOptions.map((n) => <option key={n} value={n} />)}
                  </datalist>
                </div>
              </div>

              <div>
                <Label>Responsable</Label>
                <Select value={editingPost.responsable_id || '__none__'} onValueChange={(v) => setEditingPost((p) => ({ ...p, responsable_id: v === '__none__' ? '' : v }))}>
                  <SelectTrigger data-testid="post-responsable-select"><SelectValue placeholder="Sin asignar" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">— Sin asignar —</SelectItem>
                    {team.map((m) => <SelectItem key={m.id} value={m.id}>{m.name} {m.role === 'ceo' || m.role === 'manager' ? '· ' + m.role : ''}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Responsable portada <span className="text-xs text-muted-foreground font-normal">(opcional)</span></Label>
                <Select value={editingPost.responsable_portada_id || '__none__'} onValueChange={(v) => setEditingPost((p) => ({ ...p, responsable_portada_id: v === '__none__' ? '' : v }))}>
                  <SelectTrigger data-testid="post-responsable-portada-select"><SelectValue placeholder="Sin asignar" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">— Sin asignar —</SelectItem>
                    {team.map((m) => <SelectItem key={m.id} value={m.id}>{m.name} {m.role === 'ceo' || m.role === 'manager' ? '· ' + m.role : ''}</SelectItem>)}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground mt-1">Si asignas ambos, se crearán dos tareas independientes (edición y portada) para la misma publicación.</p>
              </div>

              <div>
                <Label>Copy del post</Label>
                <Textarea
                  rows={4}
                  value={editingPost.copy || ''}
                  onChange={(e) => setEditingPost((p) => ({ ...p, copy: e.target.value }))}
                  placeholder="Texto que acompañará la publicación"
                  data-testid="post-copy-input"
                />
              </div>

              <div>
                <Label>Instrucciones para el editor</Label>
                <Textarea
                  rows={3}
                  value={editingPost.instrucciones || ''}
                  onChange={(e) => setEditingPost((p) => ({ ...p, instrucciones: e.target.value }))}
                  placeholder="Notas técnicas, transiciones, subtítulos..."
                  data-testid="post-instrucciones-input"
                />
              </div>
              </fieldset>
            </CardContent>
          </Card>

          {/* Creatividad / Adjuntos (1 col) + Historial */}
          <div className="space-y-4">
            {!clientMode && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm flex items-center gap-1.5"><ImageIcon className="w-4 h-4 text-primary" /> Portada</CardTitle>
              </CardHeader>
              <CardContent>
                {editingPost._isNew ? (
                  <p className="text-xs text-muted-foreground py-4 text-center border border-dashed rounded-md">
                    Crea primero la publicación para poder subir la portada.
                  </p>
                ) : (
                  <div className="flex items-center gap-3">
                    {editingPost.portada_file_id ? (
                      <img
                        src={contentCalendarAPI.getAttachmentUrl(editingPost.id, editingPost.portada_file_id)}
                        alt="Portada"
                        className="w-20 h-20 object-cover rounded-md border shrink-0"
                        data-testid="post-cover-preview"
                      />
                    ) : (
                      <div className="w-20 h-20 rounded-md border border-dashed flex items-center justify-center text-muted-foreground shrink-0">
                        <ImageIcon className="w-5 h-5" />
                      </div>
                    )}
                    <div className="flex flex-col items-start gap-1.5 min-w-0">
                      {editable && (
                        <>
                          <input ref={coverInputRef} type="file" accept="image/*" className="hidden" onChange={handleCoverUpload} data-testid="post-cover-input" />
                          <Button type="button" variant="outline" size="sm" onClick={() => coverInputRef.current?.click()} disabled={uploadingCover} data-testid="post-cover-upload-btn">
                            {uploadingCover ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Upload className="w-3.5 h-3.5 mr-1" />}
                            {editingPost.portada_file_id ? 'Cambiar portada' : 'Subir portada'}
                          </Button>
                          {editingPost.portada_file_id && (
                            <Button type="button" variant="ghost" size="sm" className="text-red-600 h-7" onClick={handleCoverRemove} data-testid="post-cover-remove-btn">
                              <Trash2 className="w-3.5 h-3.5 mr-1" /> Quitar
                            </Button>
                          )}
                        </>
                      )}
                      {editingPost.portada_file_id && !clientMode && (
                        <Button type="button" variant="ghost" size="sm" className="h-7" onClick={() => triggerFileDownload(contentCalendarAPI.getAttachmentDownloadUrl(editingPost.id, editingPost.portada_file_id))} data-testid="post-cover-download-btn">
                          <Download className="w-3.5 h-3.5 mr-1" /> Descargar
                        </Button>
                      )}
                      <p className="text-[11px] text-muted-foreground">Se ve como miniatura en el calendario (también en el portal del cliente).</p>
                    </div>
                  </div>
                )}
                {!editingPost._isNew && (
                  <div className="mt-4 pt-3 border-t" data-testid="post-color-cover-section">
                    <p className="text-xs font-semibold flex items-center gap-1.5 mb-2"><Palette className="w-3.5 h-3.5 text-primary" /> Color / diseño de portada</p>
                    <div className="flex items-center gap-3">
                      {editingPost.color_portada_file_id ? (
                        <img src={contentCalendarAPI.getAttachmentUrl(editingPost.id, editingPost.color_portada_file_id)} alt="Color de portada" className="w-14 h-14 object-cover rounded-md border shrink-0" data-testid="post-color-cover-preview" />
                      ) : (
                        <div className="w-14 h-14 rounded-md border border-dashed flex items-center justify-center text-muted-foreground shrink-0"><Palette className="w-4 h-4" /></div>
                      )}
                      <div className="flex flex-col items-start gap-1.5 min-w-0">
                        {editable && (
                          <>
                            <input ref={colorCoverInputRef} type="file" accept="image/*" className="hidden" onChange={handleColorCoverUpload} data-testid="post-color-cover-input" />
                            <Button type="button" variant="outline" size="sm" onClick={() => colorCoverInputRef.current?.click()} disabled={uploadingColorCover} data-testid="post-color-cover-upload-btn">
                              {uploadingColorCover ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Upload className="w-3.5 h-3.5 mr-1" />}
                              {editingPost.color_portada_file_id ? 'Cambiar' : 'Subir color / diseño'}
                            </Button>
                            {editingPost.color_portada_file_id && (
                              <Button type="button" variant="ghost" size="sm" className="text-red-600 h-7" onClick={handleColorCoverRemove} data-testid="post-color-cover-remove-btn">
                                <Trash2 className="w-3.5 h-3.5 mr-1" /> Quitar
                              </Button>
                            )}
                          </>
                        )}
                        {editingPost.color_portada_file_id && !clientMode && (
                          <Button type="button" variant="ghost" size="sm" className="h-7" onClick={() => triggerFileDownload(contentCalendarAPI.getAttachmentDownloadUrl(editingPost.id, editingPost.color_portada_file_id))} data-testid="post-color-cover-download-btn">
                            <Download className="w-3.5 h-3.5 mr-1" /> Descargar
                          </Button>
                        )}
                        <p className="text-[11px] text-muted-foreground">Se muestra como una pequeña muestra en la esquina de la miniatura del calendario.</p>
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
            )}
            <Card>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-sm">Creatividad / Material</CardTitle>
                  {!editingPost._isNew && editable && (
                    <label className="cursor-pointer">
                      <input type="file" className="hidden" onChange={handleUpload} accept="image/*,video/*" data-testid="post-upload-input" />
                      <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium bg-primary text-primary-foreground hover:opacity-90">
                        <Upload className="w-3.5 h-3.5" /> Subir
                      </span>
                    </label>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                {editingPost._isNew ? (
                  <p className="text-xs text-muted-foreground py-6 text-center border border-dashed rounded-md">
                    Crea primero la publicación para poder subir archivos.
                  </p>
                ) : attachments.length === 0 ? (
                  <p className="text-xs text-muted-foreground py-6 text-center border border-dashed rounded-md">
                    Sin archivos subidos aún
                  </p>
                ) : (
                  <div className="grid grid-cols-2 gap-2">
                    {attachments.map((a) => {
                      const kind = a.kind || (a.content_type || '').split('/')[0];
                      const url = contentCalendarAPI.getAttachmentUrl(editingPost.id, a.file_id);
                      return (
                        <div key={a.file_id} className="relative rounded-md border overflow-hidden bg-muted/20 group" data-testid={`attachment-${a.file_id}`}>
                          {kind === 'image' ? (
                            <img src={url} alt={a.name || ''} className="w-full h-24 object-cover" />
                          ) : kind === 'video' ? (
                            <div className="w-full h-24 flex items-center justify-center bg-muted">
                              <FileVideo className="w-8 h-8 text-muted-foreground" />
                            </div>
                          ) : (
                            <div className="w-full h-24 flex items-center justify-center bg-muted">
                              <FileText className="w-8 h-8 text-muted-foreground" />
                            </div>
                          )}
                          <div className="p-1.5">
                            <p className="text-[10px] truncate" title={a.name}>{a.name || 'Archivo'}</p>
                          </div>
                          {!clientMode && (
                          <button
                            onClick={() => triggerFileDownload(contentCalendarAPI.getAttachmentDownloadUrl(editingPost.id, a.file_id))}
                            className="absolute top-1 left-1 p-1 rounded-full bg-black/60 text-white hover:bg-black/80"
                            title="Descargar original"
                            data-testid={`attachment-download-${a.file_id}`}
                          >
                            <Download className="w-3 h-3" />
                          </button>
                          )}
                          {editable && (
                          <button
                            onClick={() => handleDeleteAttachment(a.file_id)}
                            className="absolute top-1 right-1 p-1 rounded-full bg-black/60 text-white opacity-0 group-hover:opacity-100 transition-opacity"
                            title="Eliminar"
                          >
                            <X className="w-3 h-3" />
                          </button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Historial de cambios de estado */}
            {!editingPost._isNew && (
              <Card data-testid="state-history-card">
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <ClockHistory className="w-4 h-4 text-primary" />
                    Historial de cambios
                  </CardTitle>
                  <p className="text-xs text-muted-foreground">
                    {(editingPost.state_history || []).length} {(editingPost.state_history || []).length === 1 ? 'evento' : 'eventos'}
                  </p>
                </CardHeader>
                <CardContent>
                  <StateHistoryPanel
                    history={editingPost.state_history || []}
                    userMap={teamNameMap}
                  />
                </CardContent>
              </Card>
            )}

            {!clientMode && user?.role === 'ceo' && !editingPost._isNew && editingPost.estado === 'cambios_solicitados' && (
              <AssignChangesPanel post={editingPost} team={team}
                onAssigned={(uid) => setEditingPost((cur) => ({ ...cur, cambios_asignados_a: uid }))} />
            )}

            {/* Observaciones del cliente (hilo de conversación) */}
            {!editingPost._isNew && (
              <Card data-testid="observations-card">
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <MessageSquare className="w-4 h-4 text-primary" />
                    Observaciones
                  </CardTitle>
                  <p className="text-xs text-muted-foreground">
                    {(editingPost.observaciones || []).length} {(editingPost.observaciones || []).length === 1 ? 'observación' : 'observaciones'}{!clientMode && ' · las ve también el cliente'}
                  </p>
                </CardHeader>
                <CardContent className="space-y-3">
                  {(editingPost.observaciones || []).length === 0 ? (
                    <p className="text-xs text-muted-foreground py-3 text-center border border-dashed rounded-md">Sin observaciones aún</p>
                  ) : (
                    <div className="space-y-2 max-h-60 overflow-y-auto">
                      {(editingPost.observaciones || []).map((o) => {
                        const isClient = o.autor_tipo === 'cliente';
                        const d = o.created_at ? new Date(o.created_at) : null;
                        const date = d && !isNaN(d.getTime()) ? d.toLocaleDateString('es-ES', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '';
                        return (
                          <div key={o.id} className={`text-sm rounded-md px-2.5 py-1.5 border ${isClient ? 'bg-amber-50 border-amber-200' : 'bg-sky-50 border-sky-200'}`} data-testid={`obs-${o.id}`}>
                            <div className="flex items-center justify-between gap-2 mb-0.5">
                              <span className={`text-[11px] font-semibold ${isClient ? 'text-amber-800' : 'text-sky-800'}`}>{o.autor_nombre || (isClient ? 'Cliente' : 'Equipo')}</span>
                              <span className="text-[10px] text-muted-foreground">{date}</span>
                            </div>
                            <p className="whitespace-pre-wrap text-foreground/90">{o.texto}</p>
                          </div>
                        );
                      })}
                    </div>
                  )}
                  {(editable || clientMode) && (
                  <div className="space-y-2 pt-1 border-t">
                    <Label className="text-xs font-medium">
                      {clientMode ? 'Escribe un comentario para el equipo' : 'Responder en el hilo'}
                    </Label>
                    <Textarea
                      value={obsReply}
                      onChange={(e) => setObsReply(e.target.value)}
                      placeholder={clientMode ? 'Ej.: Cambiad la foto de portada, se ve oscura...' : 'Escribe una respuesta para el hilo...'}
                      rows={3}
                      className="resize-none text-sm w-full"
                      data-testid="obs-reply-input"
                    />
                    <Button
                      size="sm"
                      className="w-full"
                      onClick={handleSendObservation}
                      disabled={sendingObs || !obsReply.trim()}
                      data-testid="obs-reply-send"
                    >
                      {sendingObs ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Send className="w-4 h-4 mr-1.5" />}
                      Enviar comentario
                    </Button>
                    {clientMode && (
                      <p className="text-[11px] text-muted-foreground">
                        El comentario llega al equipo al instante. Si además quieres que se rehaga la publicación, usa el botón <span className="font-medium">Solicitar cambios</span> de arriba.
                      </p>
                    )}
                  </div>
                  )}
                </CardContent>
              </Card>
            )}
            {META_DIRECT_ENABLED && !clientMode && !editingPost._isNew && (
              <MetaPublishCard post={editingPost} onStateChange={(estado, fecha) => { setEditingPost((p) => ({ ...p, estado, ...(fecha ? { fecha } : {}) })); loadPosts(); }} />
            )}
            {!clientMode && !editingPost._isNew && (
              <InternalCommentsThread postId={editingPost.id} team={team} onRead={markCommentsRead} />
            )}
          </div>
        </div>

        {/* Guion (full width) */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm">Guion estructurado · timecodes para reels</CardTitle>
            <p className="text-xs text-muted-foreground">
              Tabla de trabajo del editor. Pega una tabla desde Claude/ChatGPT y se rellena automáticamente.
            </p>
          </CardHeader>
          <CardContent>
            <fieldset disabled={!editable} className="border-0 p-0 m-0 min-w-0">
            <GuionTableEditor
              rows={Array.isArray(editingPost.guion_table) ? editingPost.guion_table : (legacyGuionToRows(editingPost.guion) || [])}
              onChange={(rows) => setEditingPost((p) => ({ ...p, guion_table: rows }))}
            />
            </fieldset>
          </CardContent>
        </Card>

        {/* Footer actions (mirror of header for long pages) */}
        <Card>
          <CardContent className="py-3 flex items-center justify-between gap-2">
            <Button variant="ghost" size="sm" onClick={() => { setEditorOpen(false); setEditingPost(null); }}>
              <ChevronLeft className="w-4 h-4 mr-1" /> Volver al calendario
            </Button>
            <div className="flex gap-2">
              {!clientMode && !editingPost._isNew && (
                <Button variant="outline" onClick={() => setIgOpen(true)} data-testid="post-editor-ig-preview-btn">
                  <Instagram className="w-4 h-4 mr-1.5" /> Vista previa Instagram
                </Button>
              )}
              {!clientMode && !editingPost._isNew && (
                <Button variant="outline" onClick={() => downloadPostsZip([editingPost.id])} data-testid="post-editor-download-all-btn">
                  <Archive className="w-4 h-4 mr-1.5" /> Descargar todo el material
                </Button>
              )}
              {editable && !editingPost._isNew && (
                <Button variant="ghost" onClick={handleDelete} className="text-destructive hover:text-destructive">
                  <Trash2 className="w-4 h-4 mr-1.5" /> Eliminar
                </Button>
              )}
              {editable && (
                <Button onClick={handleSavePost}>
                  <Save className="w-4 h-4 mr-1.5" />
                  {editingPost._isNew ? 'Crear publicación' : 'Guardar cambios'}
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
        {editingPost && !clientMode && !editingPost._isNew && (
        <TeamInstagramPreviewDialog open={igOpen} onOpenChange={setIgOpen} post={editingPost} clientId={clientId} clientName={clientName}
          Dialog={IgDialog} DialogContent={IgDialogContent} DialogHeader={IgDialogHeader} DialogTitle={IgDialogTitle} useMedia={useMedia} brandKitAPI={brandKitAPI} />
      )}
      </motion.div>
    );
  }

  // Calendar view (default)
  return (
    <TooltipProvider>
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className={fullscreen ? 'fixed inset-0 z-50 bg-background overflow-auto p-4 space-y-4' : 'space-y-4'} data-testid="client-content-calendar">
      <Card>
        <CardHeader className="pb-4">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <CardTitle className="flex items-center gap-2">
                <CalendarIcon className="w-5 h-5 text-primary" />
                Calendario de Contenido
              </CardTitle>
              {clientName && (
                <p className="text-sm text-muted-foreground mt-0.5">Cliente: <span className="font-medium text-foreground">{clientName}</span></p>
              )}
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={goPrev} data-testid="calendar-prev">
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <div className="px-3 py-1.5 rounded-md bg-muted text-sm font-medium min-w-[160px] text-center" data-testid="calendar-month-label">
                {SPANISH_MONTHS[cursor.month]} {cursor.year}
              </div>
              <Button variant="outline" size="sm" onClick={goNext} data-testid="calendar-next">
                <ChevronRight className="w-4 h-4" />
              </Button>
              <Button variant="ghost" size="sm" onClick={goToday} data-testid="calendar-today">Hoy</Button>
              <CalendarFieldsSelector visible={visibleFields} onToggle={toggleVisibleField} />
              <Button variant={fullscreen ? 'default' : 'outline'} size="sm" onClick={() => setFullscreen((v) => !v)} data-testid="calendar-fullscreen-btn">
                {fullscreen ? <Minimize2 className="w-4 h-4 mr-1.5" /> : <Maximize2 className="w-4 h-4 mr-1.5" />}
                {fullscreen ? 'Salir' : 'Pantalla completa'}
              </Button>
              {user?.role === 'ceo' && !clientMode && clientId && (
                <CsvImportButton clientId={clientId} clientName={clientName} onImported={loadPosts} />
              )}
              {isAdmin && clientId && pendingApprovalCount > 0 && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleSendReminder}
                  disabled={sendingReminder}
                  data-testid="send-approval-reminder-btn"
                  title="Enviar ahora al cliente el recordatorio de publicaciones pendientes de aprobar"
                  className="border-amber-300 text-amber-700 hover:bg-amber-50 hover:text-amber-800"
                >
                  {sendingReminder ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <BellRing className="w-4 h-4 mr-1.5" />}
                  Recordar al cliente ({pendingApprovalCount})
                </Button>
              )}
              {!clientMode && (
              <Button
                variant={onlyMine ? 'default' : 'outline'}
                size="sm"
                onClick={() => setOnlyMine((v) => !v)}
                data-testid="only-mine-toggle"
                title="Mostrar solo las publicaciones asignadas a mí"
              >
                <UserCheck className="w-4 h-4 mr-1.5" />
                Solo las mías
              </Button>
              )}
            </div>
          </div>
          {!clientMode && (
            <div className="mt-3" data-testid="calendar-download-toolbar">
              <DownloadSelectionBar selection={selection} visiblePosts={monthPosts} />
            </div>
          )}
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12 text-muted-foreground">
              <Loader2 className="w-5 h-5 animate-spin mr-2" /> Cargando...
            </div>
          ) : (
            <>
              <div className="flex flex-wrap items-center gap-1.5 mb-3 pb-3 border-b" data-testid="calendar-legend">
                <span className="text-xs font-medium text-muted-foreground mr-1">Estados:</span>
                {CONTENT_STATES.map((s) => (
                  <span key={s.id} className="inline-flex items-center gap-1 pl-1.5 pr-2 py-0.5 rounded text-[11px] font-medium border-l-4"
                    style={{ backgroundColor: s.bg, color: s.fg, borderLeftColor: s.fg }} data-testid={`state-badge-${s.id}`}>{s.label}</span>
                ))}
              </div>
              <div className="grid grid-cols-7 gap-1 mb-2">
                {SPANISH_DAYS.map((d) => (
                  <div key={d} className="text-xs font-semibold text-muted-foreground text-center py-1">{d}</div>
                ))}
              </div>
              <div className="grid grid-cols-7 gap-1.5">
                {cells.map((cell, idx) => {
                  if (!cell) return <div key={idx} className={`${fullscreen ? 'min-h-[190px]' : 'min-h-[150px]'} rounded-md bg-muted/30`} />;
                  const key = ymd(cell);
                  const today = ymd(new Date());
                  const isToday = key === today;
                  const dayPosts = postsByDate[key] || [];
                  return (
                    <div
                      key={idx}
                      onDragOver={isAdmin && !clientMode ? (e) => { e.preventDefault(); setDragOver(key); } : undefined}
                      onDragLeave={isAdmin && !clientMode ? () => setDragOver((k) => (k === key ? null : k)) : undefined}
                      onDrop={isAdmin && !clientMode ? (e) => { e.preventDefault(); setDragOver(null); movePost(e.dataTransfer.getData('text/post-id'), key); } : undefined}
                      className={`${fullscreen ? 'min-h-[190px]' : 'min-h-[150px]'} min-w-0 rounded-md border bg-muted/20 flex flex-col p-1.5 group relative transition-colors hover:border-primary/50 ${isToday ? 'ring-1 ring-primary border-primary' : 'border-border'} ${dragOver === key ? 'bg-primary/10 border-primary border-dashed' : ''}`}
                      data-testid={`calendar-cell-${key}`}
                    >
                      <div className="flex items-center justify-between mb-1.5">
                        {isAdmin ? (
                        <button
                          onClick={() => openCreate(key)}
                          className="opacity-40 group-hover:opacity-100 transition-opacity p-0.5 rounded border border-dashed border-border bg-white hover:border-primary"
                          title="Añadir publicación"
                          data-testid={`add-post-${key}`}
                        >
                          <Plus className="w-3.5 h-3.5 text-muted-foreground" />
                        </button>
                        ) : <span />}
                        <span className={`text-sm ${isToday ? 'text-primary font-bold' : 'text-muted-foreground'}`}>{cell.getDate()}</span>
                      </div>
                      <div className="flex flex-col gap-1.5">
                        {dayPosts.map((p) => (
                          <div key={p.id} className="relative group/post">
                          {!clientMode && (
                            <div className="absolute top-0.5 right-0.5 z-10 flex items-center gap-0.5">
                              {selection.active && (
                                <Checkbox
                                  checked={selection.selected.has(p.id)}
                                  onCheckedChange={() => selection.toggle(p.id)}
                                  className="h-3.5 w-3.5 bg-white"
                                  data-testid={`calendar-select-${p.id}`}
                                />
                              )}
                              <CommentsBadge info={commentInfo[p.id]} className="shadow-sm" />
                              <PostDownloadMenu post={p} className="bg-white/85 text-foreground shadow-sm" />
                            </div>
                          )}
                          <Tooltip delayDuration={350}>
                          <TooltipTrigger asChild>
                          <button
                            onClick={() => openEdit(p)}
                            draggable={isAdmin && !clientMode && p.estado !== 'publicado'}
                            onDragStart={(e) => { e.dataTransfer.setData('text/post-id', p.id); e.dataTransfer.effectAllowed = 'move'; }}
                            className={`w-full text-left rounded-lg border bg-white p-2 shadow-sm hover:shadow-md hover:border-primary/40 transition-shadow ${clientMode ? '' : selection.active ? 'pr-10' : 'pr-7'}`}
                            data-testid={`post-card-${p.id}`}
                          >
                            <PostCalendarCard post={p} team={team} showTeam={!clientMode} visible={visibleFields} locked={!canEditPost(p) && !clientMode} />
                          </button>
                          </TooltipTrigger>
                          <TooltipContent side="right" className="p-3 bg-popover text-popover-foreground border shadow-lg">
                            <PostHoverCard post={p} team={clientMode ? null : team} />
                          </TooltipContent>
                          </Tooltip>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </motion.div>
    </TooltipProvider>
  );
}
